import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle,
  Sparkles,
  ExternalLink,
  FileText,
  Receipt,
  Download,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card";
import { Badge } from "@/modules/shared/ui/components/badge";
import { Button } from "@/modules/shared/ui/components/button";
import { Spinner } from "@/modules/shared/ui/components/spinner";
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations";
import { useActivePlugins } from "@/modules/marketplace/hooks/use-active-plugins";
import { engageApi } from "../../infrastructure/engage.api";
import type {
  EngageEmployeeDto,
  DistributionRunDto,
  DistributionType,
} from "../../dtos/engage.dto";
import { sage300EmployeeImportService } from "@/modules/sage300/infrastructure/sage300EmployeeImportService";
import type { EmployeeImportStatusResponse } from "@/modules/sage300/infrastructure/sage300EmployeeImportService";
import { useSage300ImportHub } from "@/modules/sage300/hooks/useSage300ImportHub";

function extractErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null) {
    const data = (err as { response?: { data?: { message?: string } } })
      .response?.data;
    if (data?.message) return data.message;
  }
  return fallback;
}

const RUN_STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive"
> = {
  Running: "secondary",
  Completed: "default",
  Failed: "destructive",
  Cancelled: "secondary",
};

const ITEM_STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive"
> = {
  Sent: "default",
  Skipped: "secondary",
  Failed: "destructive",
};

export default function EngagePage() {
  const { currentOrganization } = useOrganizationsService();
  const organizationId = currentOrganization?.id;
  const { installs, isLoading } = useActivePlugins(organizationId);

  const engageInstall = installs.find(
    (i) => i.familyId === "engage" && i.status === "Active",
  );
  const isPlus = (engageInstall?.tier ?? 0) >= 2;
  const isEngageActive = Boolean(engageInstall);

  const [employees, setEmployees] = useState<EngageEmployeeDto[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [sendingKey, setSendingKey] = useState<string | null>(null);

  const [runs, setRuns] = useState<DistributionRunDto[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<DistributionRunDto | null>(null);
  const [startingType, setStartingType] = useState<DistributionType | null>(
    null,
  );

  const [importRunId, setImportRunId] = useState<string | null>(null);
  const [importStatus, setImportStatus] =
    useState<EmployeeImportStatusResponse | null>(null);
  const [startingImport, setStartingImport] = useState(false);
  const importing = startingImport || Boolean(importRunId);

  // Completion can arrive from two places — the SignalR push and the polling
  // fallback — so guard on the run id to apply each run's result exactly once.
  const handledRunIdRef = useRef<string | null>(null);
  const { onImportCompleted } = useSage300ImportHub(organizationId);

  const loadEmployees = useCallback(async () => {
    if (!organizationId) return;
    setEmployeesLoading(true);
    try {
      setEmployees(await engageApi.listEmployees(organizationId));
    } catch {
      toast.error("Failed to load employees.");
    } finally {
      setEmployeesLoading(false);
    }
  }, [organizationId]);

  const loadRuns = useCallback(async () => {
    if (!organizationId) return;
    try {
      const list = await engageApi.listDistributionRuns(organizationId);
      setRuns(list);
      const running = list.find((r) => r.status === "Running");
      if (running) setActiveRunId((current) => current ?? running.id);
    } catch {
      // Run history is secondary — fail silently rather than blocking the page.
    }
  }, [organizationId]);

  // Applies a terminal import result: stops the spinner, notifies, and refreshes
  // the list. Idempotent per run so the push and the poll fallback can't both fire.
  const finishImport = useCallback(
    (status: EmployeeImportStatusResponse) => {
      if (handledRunIdRef.current === status.runId) return;
      handledRunIdRef.current = status.runId;

      setImportStatus(status);
      setImportRunId(null);
      if (status.isSuccess) {
        toast.success(
          `Imported ${status.updated} employee${status.updated === 1 ? "" : "s"} from Sage 300 (${status.skipped} skipped).`,
        );
      } else {
        toast.error(status.lastErrorPayload ?? "Employee import failed.");
      }
      void loadEmployees();
    },
    [loadEmployees],
  );

  useEffect(() => {
    if (!isEngageActive) return;
    void loadEmployees();
    void loadRuns();
  }, [isEngageActive, loadEmployees, loadRuns]);

  // The import is queued server-side and outlives this component, so on mount
  // adopt the org's latest run if it's still in flight — otherwise navigating
  // away and back leaves the page looking idle mid-import.
  useEffect(() => {
    if (!isEngageActive || !organizationId) return;
    let cancelled = false;

    void (async () => {
      try {
        const latest =
          await sage300EmployeeImportService.getLatestEmployeeImportStatus(
            organizationId,
          );
        if (cancelled || !latest || latest.isTerminal) return;
        setImportStatus(latest);
        setImportRunId((current) => current ?? latest.runId);
      } catch {
        // Import status is secondary — fail silently rather than blocking the page.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEngageActive, organizationId]);

  // Real-time completion: the backend pushes the terminal result over SignalR,
  // so the finished import lands immediately without waiting on the next poll.
  useEffect(() => {
    if (!isEngageActive || !organizationId) return;
    return onImportCompleted((status) => finishImport(status));
  }, [isEngageActive, organizationId, onImportCompleted, finishImport]);

  // Poll the active run every 2s until it leaves the Running state.
  useEffect(() => {
    if (!organizationId || !activeRunId) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const run = await engageApi.getDistributionRun(
          organizationId,
          activeRunId,
        );
        if (cancelled) return;
        setActiveRun(run);
        if (run.status !== "Running") {
          setActiveRunId(null);
          void loadRuns();
          return;
        }
      } catch {
        if (!cancelled) setActiveRunId(null);
        return;
      }
      if (!cancelled) timeoutId = setTimeout(poll, 2000);
    };

    void poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [organizationId, activeRunId, loadRuns]);

  // Poll the employee import run every 2s until it reaches a terminal state.
  useEffect(() => {
    if (!organizationId || !importRunId) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = async () => {
      let status: EmployeeImportStatusResponse | null = null;
      try {
        status = await sage300EmployeeImportService.getEmployeeImportStatus(
          organizationId,
          importRunId,
        );
      } catch {
        // Transient error (network blip, read lag) — keep polling so we still
        // catch completion rather than abandoning the in-flight import.
        if (!cancelled) timeoutId = setTimeout(poll, 2000);
        return;
      }

      if (cancelled) return;

      // Fallback path if the SignalR completion push doesn't arrive (e.g. the
      // socket is down): the poll still catches the terminal result.
      if (status?.isTerminal) {
        finishImport(status);
        return;
      }

      // Still running, or a transient null response — keep the progress state
      // and poll again until the run reaches a terminal status.
      if (status) setImportStatus(status);
      if (!cancelled) timeoutId = setTimeout(poll, 2000);
    };

    void poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [organizationId, importRunId, finishImport]);

  const handleSend = async (
    employee: EngageEmployeeDto,
    type: "payslip" | "irp5",
  ) => {
    if (!organizationId) return;
    const key = `${employee.id}:${type}`;
    setSendingKey(key);
    try {
      const result =
        type === "payslip"
          ? await engageApi.sendPayslip(organizationId, employee.id)
          : await engageApi.sendIrp5(organizationId, employee.id);
      toast.success(result.message);
    } catch (err) {
      toast.error(
        extractErrorMessage(
          err,
          `Failed to send ${type === "payslip" ? "payslip" : "IRP5"} to ${employee.name}.`,
        ),
      );
    } finally {
      setSendingKey(null);
    }
  };

  const handleStartDistribution = async (type: DistributionType) => {
    if (!organizationId) return;
    setStartingType(type);
    try {
      const run = await engageApi.startDistribution(organizationId, type);
      setActiveRun(run);
      setActiveRunId(run.id);
      toast.success(
        `${type === "Payslip" ? "Payslip" : "IRP5"} distribution started.`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to start distribution."));
    } finally {
      setStartingType(null);
    }
  };

  const handleImportEmployees = async () => {
    if (!organizationId) return;
    setStartingImport(true);
    try {
      const result =
        await sage300EmployeeImportService.startEmployeeImport(organizationId);
      if (!result.success || !result.runId) {
        toast.error(result.message || "Failed to start employee import.");
        return;
      }
      setImportStatus(null);
      setImportRunId(result.runId);
      toast.info(result.message || "Employee import started.");
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Failed to start employee import."),
      );
    } finally {
      setStartingImport(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-16 flex items-center justify-center gap-2 text-muted-foreground">
        <Spinner className="w-4 h-4 animate-spin" />
        Loading…
      </div>
    );
  }

  // Reachable by direct URL even when the plugin isn't active — guide the user
  // to the marketplace instead of showing a broken/empty feature page.
  if (!engageInstall) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4 text-center space-y-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
          <MessageCircle className="w-6 h-6 text-muted-foreground" />
        </div>
        <h1 className="text-xl font-semibold">Engage isn't active</h1>
        <p className="text-sm text-muted-foreground">
          Install Engage from the marketplace to connect WhatsApp and start
          engaging your workforce.
        </p>
        <Button asChild>
          <Link to="/marketplace">Go to Marketplace</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {isPlus ? "Engage+" : "Engage"}
            <Badge variant="default">Active</Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Push payslips and IRP5s to your team over WhatsApp
            {isPlus ? ", with AI-powered insights." : "."}
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/marketplace">Manage</Link>
        </Button>
      </div>

      {activeRun && (
        <Card className="bg-card border border-primary/30 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                {activeRun.type === "Payslip" ? (
                  <Receipt className="w-5 h-5" />
                ) : (
                  <FileText className="w-5 h-5" />
                )}
                {activeRun.type === "Payslip" ? "Payslip" : "IRP5"} distribution
              </span>
              <Badge
                variant={RUN_STATUS_VARIANT[activeRun.status] ?? "secondary"}
              >
                {activeRun.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">
                {activeRun.sent + activeRun.skipped + activeRun.failed} /{" "}
                {activeRun.total} processed
              </span>
              <span className="text-primary font-medium">
                {activeRun.sent} sent
              </span>
              <span className="text-muted-foreground">
                {activeRun.skipped} skipped
              </span>
              <span className="text-destructive">
                {activeRun.failed} failed
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-border overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{
                  width: `${activeRun.total > 0 ? ((activeRun.sent + activeRun.skipped + activeRun.failed) / activeRun.total) * 100 : 0}%`,
                }}
              />
            </div>
            {activeRun.items && activeRun.items.length > 0 && (
              <div className="max-h-48 overflow-y-auto border-t pt-2 space-y-1">
                {activeRun.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between text-xs py-1"
                  >
                    <span className="text-foreground">{item.employeeName}</span>
                    <span className="flex items-center gap-2">
                      {item.reason && (
                        <span className="text-muted-foreground truncate max-w-[220px]">
                          {item.reason}
                        </span>
                      )}
                      <Badge
                        variant={
                          ITEM_STATUS_VARIANT[item.status] ?? "secondary"
                        }
                        className="text-[10px]"
                      >
                        {item.status}
                      </Badge>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border border-border shadow-sm">
        <CardHeader className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Employees
          </CardTitle>
          <Button
            size="sm"
            variant="link"
            disabled={startingType !== null || Boolean(activeRunId) || importing}
            onClick={() => handleImportEmployees()}
          >
            {importing ? (
              <Spinner className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {importing
              ? "Importing from Sage 300…"
              : "Import Employees from Sage 300"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {importing && (
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4 animate-spin shrink-0" />
              <span>
                Importing employees from Sage 300…
                {importStatus && importStatus.total > 0
                  ? ` ${importStatus.updated + importStatus.skipped} / ${importStatus.total} processed`
                  : " this may take a moment."}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="flex items-between">
              <Button
                size="sm"
                variant="outline"
                disabled={startingType !== null || Boolean(activeRunId)}
                onClick={() => handleStartDistribution("Payslip")}
              >
                {startingType === "Payslip" ? "Starting…" : "Send All Payslips"}
              </Button>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={startingType !== null || Boolean(activeRunId)}
              onClick={() => handleStartDistribution("Irp5")}
            >
              {startingType === "Irp5" ? "Starting…" : "Send All IRP5s"}
            </Button>
          </div>

          {employeesLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Spinner className="w-4 h-4 animate-spin" />
              Loading employees…
            </div>
          ) : employees.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No employees found.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {employees.map((employee) => {
                const payslipKey = `${employee.id}:payslip`;
                const irp5Key = `${employee.id}:irp5`;
                return (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {employee.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {employee.phone ?? "No phone number on record"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!employee.phone || sendingKey !== null}
                        onClick={() => handleSend(employee, "payslip")}
                      >
                        {sendingKey === payslipKey
                          ? "Sending…"
                          : "Send Payslip"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!employee.phone || sendingKey !== null}
                        onClick={() => handleSend(employee, "irp5")}
                      >
                        {sendingKey === irp5Key ? "Sending…" : "Send IRP5"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {runs.length > 0 && (
        <Card className="bg-card border border-border shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Recent distributions</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-border">
            {runs.slice(0, 5).map((run) => (
              <div
                key={run.id}
                className="flex items-center justify-between py-2 text-sm"
              >
                <span>
                  {run.type === "Payslip" ? "Payslip" : "IRP5"} —{" "}
                  {new Date(run.startedAt).toLocaleString()}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">
                    {run.sent} sent · {run.skipped} skipped · {run.failed}{" "}
                    failed
                  </span>
                  <Badge
                    variant={RUN_STATUS_VARIANT[run.status] ?? "secondary"}
                  >
                    {run.status}
                  </Badge>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {isPlus && (
        <Card className="bg-card border border-border shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              AI insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Engage+ analyses responses with retrieval-augmented AI to surface
              themes, sentiment, and recommended actions.
            </p>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <ExternalLink className="w-3 h-3" />
        Manage this add-on and its connection from the Marketplace.
      </p>
    </div>
  );
}
